import { useEffect, useState } from "react";
import WellbeingCheck from "./WellbeingCheck";
import { AnimatePresence, motion } from "framer-motion";
import { X, Sparkles, ArrowRight } from "lucide-react";

const SEEN_KEY = "ams_wellbeing_invite_seen_v1";
const INVITE_DELAY_MS = 8000;

/**
 * Globally mounts the WellbeingCheck modal and exposes two entry points:
 *  1. Window event: window.dispatchEvent(new Event('open-wellbeing'))
 *  2. First-time visit "soft invite" — a small floating card 8s after page load,
 *     dismissable, never shown again (localStorage).
 */
export default function WellbeingLauncher() {
  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  // Global event listener so any component can pop the check
  useEffect(() => {
    const handler = () => {
      setInviteOpen(false);
      setOpen(true);
    };
    window.addEventListener("open-wellbeing", handler);
    return () => window.removeEventListener("open-wellbeing", handler);
  }, []);

  // First-visit soft invite
  useEffect(() => {
    let timer;
    try {
      if (localStorage.getItem(SEEN_KEY) === "1") return undefined;
      timer = setTimeout(() => setInviteOpen(true), INVITE_DELAY_MS);
    } catch {
      // localStorage unavailable — skip the invite quietly
    }
    return () => timer && clearTimeout(timer);
  }, []);

  const dismissInvite = () => {
    setInviteOpen(false);
    try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* ignore */ }
  };

  const acceptInvite = () => {
    dismissInvite();
    setOpen(true);
  };

  return (
    <>
      <WellbeingCheck open={open} onClose={() => setOpen(false)} />

      <AnimatePresence>
        {inviteOpen && !open && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            data-testid="wellbeing-invite-card"
            className="fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-[70] w-[330px] max-w-[calc(100vw-2rem)] rounded-3xl bg-cream shadow-[0_30px_60px_-15px_rgba(14,59,74,0.45)] border border-sand/70 overflow-hidden"
          >
            <div className="bg-navy-deep text-cream px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em]">
                <Sparkles size={13} /> 2-min wellbeing check
              </div>
              <button
                type="button"
                onClick={dismissInvite}
                aria-label="Dismiss"
                data-testid="wellbeing-invite-dismiss"
                className="h-7 w-7 rounded-full hover:bg-cream/15 flex items-center justify-center"
              >
                <X size={14} />
              </button>
            </div>
            <div className="p-5">
              <div className="font-serif text-xl text-ink leading-snug mb-2">How are you, really?</div>
              <p className="text-sm text-ink-muted leading-relaxed font-light mb-4">
                Take a quiet moment to reflect on your last fortnight — ten short
                questions, your answers stay private.
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={acceptInvite}
                  data-testid="wellbeing-invite-accept"
                  className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-4 py-2.5 text-sm font-medium hover:bg-navy-deep hover:-translate-y-0.5 transition"
                >
                  Start the check <ArrowRight size={14} />
                </button>
                <button
                  type="button"
                  onClick={dismissInvite}
                  data-testid="wellbeing-invite-maybe-later"
                  className="text-xs text-ink-soft hover:text-ink underline-offset-4 hover:underline"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
